import React, { useState, useEffect } from "react";
import "./MonthlyReport.css";

function SafeLogo({ src, alt, style, fallback = null }) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return fallback;
  }

  return (
    <img
      src={src}
      alt={alt}
      style={style}
      onError={() => setHasError(true)}
    />
  );
}

export default function MonthlyReportPreview({ report, theme = "light" }) {
  if (!report) return null;

  const isDark = theme === "dark";

  const metrics = report.metrics_data || {};
  const meta = metrics.metadata || {};
  const narrative = report.narrative_data || {};
  const branding = report.agency_branding || {};

  const companyName = report.company_name || meta.company_name || "Client Website";
  const periodLabel = report.period_label || meta.period_label || "Selected Monthly Period";
  const reportMonth = report.report_month || meta.report_month || "Monthly Report";
  const agencyName = (branding.agency_name !== undefined && branding.agency_name !== null) ? branding.agency_name : "";
  const accentColor = branding.accent_color || (isDark ? "#2BD1E4" : "#1FB6C4");
  const agencyLogoUrl = branding.logo_url;
  const companyLogoUrl = report.company_logo_url || narrative.company_logo_url || branding.company_logo_url;

  const statusPill = narrative.status_pill || "Overall performance: Active";
  const execSummary = narrative.executive_summary || "";
  const chips = narrative.chips || [];

  // Section 01
  const sec01 = metrics.section_01_website_traffic || {};
  const sec01Stats = sec01.stat_strip || [];
  const channels = sec01.channels || [];
  const countries = sec01.countries || [];
  const trafficNarrative = narrative.website_traffic_narrative || "";

  // Section 02
  const sec02 = metrics.section_02_google_search || {};
  const sec02Stats = sec02.stat_strip || [];
  const gscNarrative = narrative.gsc_narrative || "";
  const topQueries = sec02.top_queries || [];
  const topPages = sec02.top_pages || [];
  const queriesUnavailableReason = sec02.queries_unavailable_reason || "Top search queries are unavailable because Search Console recorded 0 query clicks for this period.";
  const pagesUnavailableReason = sec02.pages_unavailable_reason || "Top pages are unavailable because Search Console recorded 0 page clicks for this period.";

  // Section 03: AI Readiness (formerly Section 04)
  const sec04 = metrics.section_03_ai_readiness || metrics.section_04_ai_readiness || {};
  const score = sec04.overall_score !== undefined ? sec04.overall_score : 65;
  const tier = sec04.tier || "Good";
  const aiNarrative = narrative.ai_readiness_narrative || "";
  const categories = sec04.categories || [];
  const checklist = sec04.checklist || [];
  const keStats = sec04.knowledge_engine?.stat_strip || [];

  // SVG Gauge calculations (circumference for r=46 is 2 * Math.PI * 46 ≈ 289)
  const dashOffset = Math.round(289 - (289 * score) / 100);

  // Section 04: AI Referral Traffic (formerly Section 05)
  const sec05 = metrics.section_04_ai_referral || metrics.section_05_ai_referral || {};
  const sec05Stats = sec05.stat_strip || [];
  const aiLeads = sec05.leads || [];
  const aiLandingPages = sec05.landing_pages || [];
  const aiSessionsCount = sec05.ai_sessions_count || 0;
  const engagedSessionsCount = sec05.engaged_sessions_count || 0;
  const eventsCount = sec05.events_count || 0;

  // Section 05: Work Completed (formerly Section 08)
  const workCompleted = narrative.work_completed || {};
  const onPage = workCompleted.on_page || [];
  const offPage = workCompleted.off_page || [];
  const technical = workCompleted.technical || [];

  // Section 06: Priorities (formerly Section 09)
  const priorities = narrative.next_priorities || [];

  // Growth Plan
  const growthPlan = narrative.growth_plan || {};
  const isGpHidden = Boolean(growthPlan.hidden);
  const gpTitle = growthPlan.title || "Your Search & AI Growth Plan";
  const gpSubtitle = growthPlan.subtitle !== undefined 
    ? growthPlan.subtitle 
    : `What we're doing for ${companyName || "your business"} next month`;

  let gpSections = [];
  if (Array.isArray(growthPlan.sections)) {
    gpSections = growthPlan.sections;
  } else {
    const legacyPillars = [
      { id: "seo", title: "SEO", items: growthPlan.seo || [] },
      { id: "ai_visibility", title: "AI visibility", items: growthPlan.ai_visibility || [] },
      { id: "authority", title: "Authority", items: growthPlan.authority || [] },
      { id: "technical", title: "Technical", items: growthPlan.technical || [] }
    ];
    gpSections = legacyPillars.filter(p => p.items.length > 0 || !growthPlan.sections);
  }

  // Max landing page sessions for mini bar calculation
  const maxLpSessions = Math.max(...(aiLandingPages.map(lp => lp.sessions || 1)), 1);

  return (
    <div className={`report-mockup-wrapper ${isDark ? "theme-dark" : "theme-light"}`}>
      <style dangerouslySetInnerHTML={{ __html: `
        .report-mockup-wrapper {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          box-sizing: border-box;
          text-align: left;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 10px 36px rgba(0, 0, 0, 0.12);
        }
        .report-mockup-wrapper * { box-sizing: border-box; }

        /* ================= LIGHT THEME VARIABLES & BASE ================= */
        .report-mockup-wrapper.theme-light {
          --navy: #0B1C33;
          --bg: #F6F7FA;
          --surface: #FFFFFF;
          --cyan: #1FB6C4;
          --purple: #6C4CC4;
          --green: #1E9E6B;
          --amber: #C7821A;
          --red: #C24545;
          --border: #E4E7ED;
          --ink: #1B2230;
          --ink-muted: #5B6472;
          --ink-faint: #8A93A3;
          background: var(--bg);
          color: var(--ink);
        }
        .report-mockup-wrapper.theme-light .brand-band {
          background: var(--navy);
          background-image: linear-gradient(120deg, var(--navy) 0%, #142645 60%, #1B2A55 100%);
          width: 100%;
        }
        .report-mockup-wrapper.theme-light .accent-rule {
          height: 3px;
          background: linear-gradient(90deg, var(--cyan), var(--purple));
          width: 100%;
        }
        .report-mockup-wrapper.theme-light .report-page {
          background: var(--bg);
          color: var(--ink);
        }
        .report-mockup-wrapper.theme-light .report-hero h1 {
          font-family: 'Fraunces', serif;
          font-size: 38px;
          font-weight: 500;
          color: var(--navy);
          letter-spacing: -0.02em;
        }

        /* ================= DARK THEME VARIABLES & BASE ================= */
        .report-mockup-wrapper.theme-dark {
          --bg: #0A0E1A;
          --card: #131A2C;
          --card-alt: #161E33;
          --line: #242D45;
          --cyan: #2BD1E4;
          --purple: #8E6CF5;
          --green: #3EDD97;
          --amber: #F0B429;
          --red: #FF6B5B;
          --text: #F2F4F9;
          --text-muted: #9CA6BD;
          --text-faint: #69728A;
          background: var(--bg);
          color: var(--text);
        }
        .report-mockup-wrapper.theme-dark .brand-band {
          background: var(--bg);
          background-image: radial-gradient(1200px 300px at 10% 0%, #141C36 0%, var(--bg) 60%);
          border-bottom: 1px solid var(--line);
          width: 100%;
        }
        .report-mockup-wrapper.theme-dark .accent-rule {
          height: 2px;
          background: var(--cyan);
          width: 100%;
        }
        .report-mockup-wrapper.theme-dark .report-page {
          background: var(--bg);
          color: var(--text);
        }
        .report-mockup-wrapper.theme-dark .report-hero h1 {
          font-weight: 800;
          font-size: 40px;
          line-height: 1.1;
          letter-spacing: -0.01em;
          color: #ffffff;
        }

        /* Top Band Inner Elements */
        .report-mockup-wrapper .brand-band-inner {
          max-width: 920px;
          margin: 0 auto;
          padding: 22px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .report-mockup-wrapper .brand-mark {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .report-mockup-wrapper .brand-mark .mark {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
        }
        .report-mockup-wrapper .brand-mark .wordmark {
          font-size: 16px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: -0.01em;
          line-height: 1.2;
        }
        .report-mockup-wrapper .brand-mark .wordmark span {
          color: var(--cyan);
        }
        .report-mockup-wrapper .brand-mark .tagline {
          font-size: 11px;
          color: #9AA6BC;
          margin-top: 1px;
        }
        .report-mockup-wrapper .prepared-for {
          text-align: right;
          font-size: 12px;
          color: #9AA6BC;
        }
        .report-mockup-wrapper.theme-dark .prepared-for {
          font-size: 11px;
          color: var(--text-faint);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .report-mockup-wrapper .prepared-for strong {
          color: #E7EAF2;
          font-weight: 600;
          display: block;
          font-size: 13px;
          margin-top: 2px;
          text-transform: none;
          letter-spacing: 0;
        }

        /* Inner Page Container */
        .report-mockup-wrapper .report-page {
          max-width: 920px;
          margin: 0 auto;
          padding: 0 28px 80px;
        }

        /* Topbar */
        .report-mockup-wrapper .report-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 0;
          border-bottom: 1px solid var(--border, #E4E7ED);
        }
        .report-mockup-wrapper.theme-dark .report-topbar {
          border-bottom: 1px solid var(--line);
          font-size: 11.5px;
          color: var(--text-faint);
        }
        .report-mockup-wrapper .report-agency-mark {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .report-mockup-wrapper .report-logo-swatch {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          flex-shrink: 0;
        }
        .report-mockup-wrapper .report-agency-mark span {
          font-size: 13px;
          font-weight: 600;
          color: var(--ink-muted, #5B6472);
        }
        .report-mockup-wrapper.theme-dark .report-agency-mark span {
          color: var(--text-muted);
        }
        .report-mockup-wrapper .report-powered-by {
          font-size: 11.5px;
          color: var(--ink-faint, #8A93A3);
        }
        .report-mockup-wrapper.theme-dark .report-powered-by {
          color: var(--text-faint);
        }

        /* Data Key */
        .report-mockup-wrapper .report-data-key {
          display: flex;
          gap: 18px;
          flex-wrap: wrap;
          padding: 14px 0 0;
          margin-bottom: -6px;
          font-size: 11.5px;
          color: var(--ink-faint, #8A93A3);
        }
        .report-mockup-wrapper.theme-dark .report-data-key {
          color: var(--text-faint);
          gap: 20px;
        }
        .report-mockup-wrapper .report-data-key span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .report-mockup-wrapper .report-data-key .sw {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          display: inline-block;
        }

        /* Hero */
        .report-mockup-wrapper .report-hero {
          padding: 40px 0 28px;
        }
        .report-mockup-wrapper .report-hero-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 24px;
        }
        .report-mockup-wrapper .report-hero-text {
          flex: 1;
          min-width: 0;
        }
        .report-mockup-wrapper .report-hero-logo {
          flex-shrink: 0;
          max-width: 220px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .report-mockup-wrapper .report-eyebrow {
          font-size: 11.5px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-weight: 600;
          color: var(--cyan);
          margin: 0 0 10px;
        }
        .report-mockup-wrapper.theme-dark .report-eyebrow {
          color: var(--text-faint);
          letter-spacing: 2px;
        }
        .report-mockup-wrapper .report-hero h1 {
          margin: 0 0 10px;
          line-height: 1.15;
        }
        .report-mockup-wrapper .report-client-line {
          font-size: 13.5px;
          color: var(--ink-muted, #5B6472);
          margin: 0 0 20px;
        }
        .report-mockup-wrapper.theme-dark .report-client-line {
          font-size: 12.5px;
          color: var(--text-faint);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .report-mockup-wrapper .report-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #EEF8F6;
          border: 1px solid #C8ECE3;
          border-radius: 100px;
          padding: 6px 14px;
          font-size: 12.5px;
          color: #12634D;
          font-weight: 500;
        }
        .report-mockup-wrapper .report-status-pill .dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #1E9E6B;
          box-shadow: 0 0 0 3px rgba(30, 158, 107, 0.2);
        }
        /* Dark mode pill */
        .report-mockup-wrapper.theme-dark .report-status-pill.dark-pill {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 18px;
          border-radius: 8px;
          background: var(--card-alt);
          border: 1px solid var(--line);
        }
        .report-mockup-wrapper.theme-dark .report-status-pill.dark-pill .pill-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--cyan);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .report-mockup-wrapper.theme-dark .report-status-pill.dark-pill .pill-divider {
          width: 1px;
          height: 14px;
          background: var(--line);
        }
        .report-mockup-wrapper.theme-dark .report-status-pill.dark-pill .pill-val {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-muted);
        }

        /* Executive Summary */
        .report-mockup-wrapper .report-exec {
          padding: 0 0 42px;
          border-bottom: 1px solid var(--border, #E4E7ED);
        }
        .report-mockup-wrapper.theme-dark .report-exec {
          border-bottom: 1px solid var(--line);
        }
        .report-mockup-wrapper .report-exec p {
          font-size: 15px;
          line-height: 1.7;
          color: var(--ink-muted, #5B6472);
          margin: 0 0 20px;
          max-width: 78ch;
        }
        .report-mockup-wrapper.theme-dark .report-exec p {
          font-size: 15.5px;
          color: var(--text-muted);
        }
        .report-mockup-wrapper .report-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .report-mockup-wrapper .report-chip {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          background: var(--surface, #FFFFFF);
          border: 1px solid var(--border, #E4E7ED);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 12.5px;
          color: var(--ink, #1B2230);
          flex: 1 1 220px;
        }
        .report-mockup-wrapper.theme-dark .report-chip {
          background: var(--card);
          border: 1px solid var(--line);
          color: var(--text);
        }
        .report-mockup-wrapper .report-chip .chip-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          margin-top: 6px;
          flex-shrink: 0;
        }
        .report-mockup-wrapper .report-chip.win .chip-dot { background: var(--green, #1E9E6B); }
        .report-mockup-wrapper .report-chip.watch .chip-dot { background: var(--amber, #C7821A); }

        /* Report Sections */
        .report-mockup-wrapper section.report-section {
          padding: 42px 0 36px;
          border-bottom: 1px solid var(--border, #E4E7ED);
        }
        .report-mockup-wrapper.theme-dark section.report-section {
          border-bottom: 1px solid var(--line);
          padding: 40px 0;
        }
        .report-mockup-wrapper section.report-section:last-of-type {
          border-bottom: none;
        }

        /* Light mode section header */
        .report-mockup-wrapper.theme-light .report-section-head {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 22px;
          padding-bottom: 12px;
          border-bottom: 2px solid transparent;
        }
        .report-mockup-wrapper.theme-light .report-section-head.src-google { border-bottom-color: var(--cyan); }
        .report-mockup-wrapper.theme-light .report-section-head.src-ai { border-bottom-color: var(--purple); }
        .report-mockup-wrapper.theme-light .report-section-head.src-thirdparty { border-bottom-color: var(--ink-faint); }
        .report-mockup-wrapper.theme-light .report-section-head .report-section-num {
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 500;
          color: var(--navy);
          line-height: 1;
        }
        .report-mockup-wrapper.theme-light .report-section-head h2 {
          font-family: 'Fraunces', serif;
          font-size: 21px;
          font-weight: 600;
          color: var(--navy);
          margin: 0;
          flex: 1;
        }
        .report-mockup-wrapper.theme-light .report-section-head .report-section-source {
          font-size: 11px;
          font-weight: 600;
          color: var(--ink-faint);
          padding: 4px 10px;
          background: #FFFFFF;
          border: 1px solid var(--border);
          border-radius: 100px;
          white-space: nowrap;
        }

        /* Dark mode section header (Card style) */
        .report-mockup-wrapper.theme-dark .report-section-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          background: var(--card-alt);
          border-left: 4px solid var(--cyan);
          border-radius: 6px;
          padding: 18px 22px;
          margin-bottom: 26px;
        }
        .report-mockup-wrapper.theme-dark .report-section-head.src-ai { border-left-color: var(--purple); }
        .report-mockup-wrapper.theme-dark .report-section-head.src-thirdparty { border-left-color: var(--text-faint); }
        .report-mockup-wrapper.theme-dark .report-section-head .report-section-num {
          font-size: 11px;
          font-weight: 700;
          color: var(--cyan);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          display: block;
          margin-bottom: 4px;
        }
        .report-mockup-wrapper.theme-dark .report-section-head.src-ai .report-section-num { color: var(--purple); }
        .report-mockup-wrapper.theme-dark .report-section-head.src-thirdparty .report-section-num { color: var(--text-faint); }
        .report-mockup-wrapper.theme-dark .report-section-head h2 {
          font-weight: 700;
          font-size: 21px;
          color: #ffffff;
          margin: 0;
        }
        .report-mockup-wrapper.theme-dark .report-section-head .report-section-source {
          font-size: 11px;
          color: var(--text-faint);
          padding: 5px 11px;
          border: 1px solid var(--line);
          border-radius: 100px;
          white-space: nowrap;
          height: fit-content;
        }

        /* Subsections */
        .report-mockup-wrapper .subsection-title {
          font-size: 13px;
          font-weight: 700;
          color: var(--navy, #0B1C33);
          text-transform: uppercase;
          letter-spacing: 0.75px;
          margin: 28px 0 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .report-mockup-wrapper .subsection-title::before {
          content: "";
          display: inline-block;
          width: 4px;
          height: 14px;
          background: var(--cyan, #1FB6C4);
          border-radius: 2px;
          flex-shrink: 0;
        }
        .report-mockup-wrapper.theme-dark .subsection-title {
          color: #F1F5F9;
          margin: 28px 0 12px;
        }
        .report-mockup-wrapper.theme-dark .subsection-title::before {
          background: var(--cyan, #2BD1E4);
        }

        /* Stat Strip */
        .report-mockup-wrapper .report-stat-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin: 16px 0 24px;
        }
        .report-mockup-wrapper.theme-light .report-stat {
          flex: 1;
          min-width: 140px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 16px 18px;
        }
        .report-mockup-wrapper.theme-dark .report-stat {
          flex: 1;
          min-width: 150px;
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 16px 18px;
        }
        .report-mockup-wrapper .report-stat .stat-label {
          font-size: 11.5px;
          color: var(--ink-faint, #8A93A3);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }
        .report-mockup-wrapper.theme-dark .report-stat .stat-label {
          font-size: 11px;
          color: var(--text-faint);
        }
        .report-mockup-wrapper.theme-light .report-stat .stat-value {
          font-family: 'Fraunces', serif;
          font-size: 26px;
          font-weight: 600;
          color: var(--navy);
          line-height: 1;
        }
        .report-mockup-wrapper.theme-dark .report-stat .stat-value {
          font-size: 26px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
        }
        .report-mockup-wrapper .report-stat .stat-delta {
          font-size: 12px;
          margin-top: 8px;
          font-weight: 600;
        }
        .report-mockup-wrapper .report-stat .stat-delta.up { color: var(--green, #1E9E6B); }
        .report-mockup-wrapper .report-stat .stat-delta.down { color: var(--red, #C24545); }
        .report-mockup-wrapper .report-stat .stat-delta.flat { color: var(--ink-faint, #8A93A3); }

        /* Narratives */
        .report-mockup-wrapper .report-narrative {
          font-size: 14px;
          line-height: 1.7;
          color: var(--ink-muted, #5B6472);
          max-width: 74ch;
          margin: 0 0 20px;
        }
        .report-mockup-wrapper.theme-dark .report-narrative {
          font-size: 14.5px;
          color: var(--text-muted);
          margin: 0 0 24px;
        }
        .report-mockup-wrapper.theme-dark .report-narrative strong {
          color: var(--text);
        }

        /* Tables */
        .report-mockup-wrapper .report-table-wrap {
          width: 100%;
          overflow-x: auto;
          margin: 10px 0 28px;
          border-radius: 8px;
          border: 1.5px solid #CBD5E1;
          background: #FFFFFF;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
          -webkit-overflow-scrolling: touch;
        }
        .report-mockup-wrapper.theme-dark .report-table-wrap {
          border-color: #2D3748;
          background: #111827;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
        }
        .report-mockup-wrapper table.report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          margin-bottom: 0;
        }
        .report-mockup-wrapper.theme-light table.report-table th {
          text-align: left;
          font-weight: 700;
          color: #FFFFFF;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          padding: 12px 14px;
          background: #0B1C33;
          border-bottom: 2.5px solid #1FB6C4;
          white-space: nowrap;
        }
        .report-mockup-wrapper.theme-dark table.report-table th {
          text-align: left;
          font-weight: 700;
          color: #FFFFFF;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          padding: 12px 14px;
          background: #162036;
          border-bottom: 2.5px solid #2BD1E4;
          white-space: nowrap;
        }
        .report-mockup-wrapper table.report-table th:first-child { border-radius: 6px 0 0 0; }
        .report-mockup-wrapper table.report-table th:last-child { border-radius: 0 6px 0 0; }
        .report-mockup-wrapper.theme-light table.report-table td {
          padding: 11px 14px;
          border-bottom: 1px solid #E2E8F0;
          color: #0F172A;
          font-size: 13px;
          line-height: 1.45;
        }
        .report-mockup-wrapper.theme-dark table.report-table td {
          padding: 11px 14px;
          border-bottom: 1px solid #1E293B;
          color: #F1F5F9;
          font-size: 13px;
          line-height: 1.45;
        }
        .report-mockup-wrapper table.report-table td:first-child {
          font-weight: 600;
        }
        .report-mockup-wrapper.theme-light table.report-table tr:nth-child(even) td { background: #F8FAFC; }
        .report-mockup-wrapper.theme-light table.report-table tr:hover td { background: #EFF6FF; }
        .report-mockup-wrapper.theme-dark table.report-table tr:nth-child(even) td { background: rgba(255, 255, 255, 0.03); }
        .report-mockup-wrapper.theme-dark table.report-table tr:hover td { background: rgba(43, 209, 228, 0.07); }
        .report-mockup-wrapper table.report-table tr:last-child td { border-bottom: none; }
        .report-mockup-wrapper table.report-table td.num {
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          white-space: nowrap;
        }
        .report-mockup-wrapper.theme-light table.report-table td.num {
          color: #0F172A;
        }
        .report-mockup-wrapper.theme-dark table.report-table td.num {
          color: #F8FAFC;
        }
        .report-mockup-wrapper .table-period-badge {
          display: inline-block;
          padding: 2px 7px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          background: #EFF6FF;
          color: #1E40AF;
          border: 1px solid #BFDBFE;
          white-space: nowrap;
        }
        .report-mockup-wrapper.theme-dark .table-period-badge {
          background: rgba(43, 209, 228, 0.12);
          color: #38BDF8;
          border: 1px solid rgba(43, 209, 228, 0.3);
        }

        /* Readiness Score Block */
        .report-mockup-wrapper .report-score-block {
          display: flex;
          gap: 36px;
          align-items: center;
          margin: 18px 0 28px;
          flex-wrap: wrap;
        }
        .report-mockup-wrapper.theme-light .report-score-block {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px 28px;
        }
        .report-mockup-wrapper.theme-dark .report-score-block {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 24px 26px;
        }
        .report-mockup-wrapper .report-score-ring {
          position: relative;
          width: 110px !important;
          height: 110px !important;
          min-width: 110px !important;
          min-height: 110px !important;
          max-width: 110px !important;
          max-height: 110px !important;
          flex-shrink: 0;
        }
        .report-mockup-wrapper .report-score-ring svg {
          width: 110px !important;
          height: 110px !important;
          display: block;
        }
        .report-mockup-wrapper .report-score-ring .report-score-num {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .report-mockup-wrapper.theme-light .report-score-ring .report-score-num .n {
          font-family: 'Fraunces', serif;
          font-size: 26px;
          font-weight: 600;
          color: var(--navy);
          line-height: 1;
        }
        .report-mockup-wrapper.theme-dark .report-score-ring .report-score-num .n {
          font-size: 26px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
        }
        .report-mockup-wrapper .report-score-ring .report-score-num .l {
          font-size: 10.5px;
          color: var(--ink-faint, #8A93A3);
          margin-top: 4px;
        }
        .report-mockup-wrapper.theme-dark .report-score-ring .report-score-num .l {
          color: var(--text-faint);
        }
        .report-mockup-wrapper .report-score-bars {
          flex: 1;
          min-width: 280px;
        }
        .report-mockup-wrapper .report-score-bar-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          font-size: 12.5px;
        }
        .report-mockup-wrapper .report-score-bar-row .b-label {
          width: 180px;
          color: var(--ink-muted, #5B6472);
          flex-shrink: 0;
        }
        .report-mockup-wrapper.theme-dark .report-score-bar-row .b-label {
          color: var(--text-muted);
        }
        .report-mockup-wrapper .report-score-bar-row .b-track {
          flex: 1;
          height: 6px;
          background: var(--border, #E4E7ED);
          border-radius: 4px;
          overflow: hidden;
        }
        .report-mockup-wrapper.theme-dark .report-score-bar-row .b-track {
          background: var(--line);
        }
        .report-mockup-wrapper .report-score-bar-row .b-fill {
          display: block;
          height: 100%;
          border-radius: 4px;
        }
        .report-mockup-wrapper .report-score-bar-row .b-val {
          width: 36px;
          text-align: right;
          color: var(--ink, #1B2230);
          font-weight: 600;
          font-size: 12px;
        }
        .report-mockup-wrapper.theme-dark .report-score-bar-row .b-val {
          color: #ffffff;
          font-weight: 700;
        }

        /* Check Grid */
        .report-mockup-wrapper .report-check-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 24px;
          margin-bottom: 26px;
        }
        .report-mockup-wrapper.theme-light .report-check-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid var(--border);
          font-size: 13px;
        }
        .report-mockup-wrapper.theme-dark .report-check-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 8px;
          font-size: 13px;
        }
        .report-mockup-wrapper .report-check-item .ci-label {
          display: flex;
          flex-direction: column;
        }
        .report-mockup-wrapper .report-check-item .ci-label .ci-sub {
          font-size: 11.5px;
          color: var(--ink-faint, #8A93A3);
        }
        .report-mockup-wrapper.theme-dark .report-check-item .ci-label .ci-sub {
          color: var(--text-faint);
          font-size: 11px;
        }
        .report-mockup-wrapper .status-tag {
          font-size: 11px;
          font-weight: 600;
          padding: 3px 9px;
          border-radius: 100px;
          white-space: nowrap;
        }
        .report-mockup-wrapper.theme-light .status-tag.found { background: #EAF7F1; color: #166B48; }
        .report-mockup-wrapper.theme-light .status-tag.missing { background: #FBEBEA; color: #A23A32; }
        .report-mockup-wrapper.theme-dark .status-tag {
          font-size: 10.5px;
          font-weight: 700;
          padding: 4px 10px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .report-mockup-wrapper.theme-dark .status-tag.found { background: rgba(62, 221, 151, 0.12); color: var(--green); }
        .report-mockup-wrapper.theme-dark .status-tag.missing { background: rgba(255, 107, 91, 0.12); color: var(--red); }

        /* Locked Panels */
        .report-mockup-wrapper .report-locked-panel {
          border: 1px dashed var(--border, #E4E7ED);
          border-radius: 12px;
          padding: 26px 28px;
          display: flex;
          gap: 18px;
          align-items: flex-start;
          margin-top: 8px;
        }
        .report-mockup-wrapper.theme-dark .report-locked-panel {
          background: var(--card);
          border: 1px dashed var(--line);
          border-radius: 10px;
        }
        .report-mockup-wrapper .report-locked-panel .lock-icon {
          flex-shrink: 0;
          width: 34px;
          height: 34px;
          border-radius: 9px;
          background: #F1EDFB;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .report-mockup-wrapper.theme-dark .report-locked-panel .lock-icon {
          background: rgba(142, 108, 245, 0.14);
        }
        .report-mockup-wrapper.theme-light .report-locked-panel h3 {
          font-family: 'Fraunces', serif;
          font-size: 16.5px;
          color: var(--navy);
          margin: 0 0 6px;
          font-weight: 600;
        }
        .report-mockup-wrapper.theme-dark .report-locked-panel h3 {
          font-size: 15.5px;
          color: #ffffff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .report-mockup-wrapper .report-locked-panel p {
          font-size: 13.5px;
          color: var(--ink-muted, #5B6472);
          line-height: 1.6;
          margin: 0;
          max-width: 58ch;
        }
        .report-mockup-wrapper.theme-dark .report-locked-panel p {
          font-size: 13px;
          color: var(--text-muted);
        }
        .report-mockup-wrapper .report-locked-panel .badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          color: var(--purple, #6C4CC4);
          background: #F1EDFB;
          padding: 3px 9px;
          border-radius: 100px;
          margin-bottom: 9px;
        }
        .report-mockup-wrapper.theme-dark .report-locked-panel .badge {
          background: rgba(142, 108, 245, 0.14);
        }

        /* AI Widgets */
        .report-mockup-wrapper .ai-widget-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
          margin-top: 22px;
        }
        .report-mockup-wrapper.theme-dark .ai-widget-grid {
          gap: 16px;
          margin-top: 24px;
        }
        .report-mockup-wrapper.theme-light .ai-widget {
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 16px 18px;
          background: var(--surface);
        }
        .report-mockup-wrapper.theme-dark .ai-widget {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 16px 18px;
        }
        .report-mockup-wrapper .ai-widget-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--ink-muted, #5B6472);
          margin-bottom: 12px;
        }
        .report-mockup-wrapper.theme-dark .ai-widget-title {
          font-size: 11px;
          font-weight: 700;
          color: var(--text-faint);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 14px;
        }
        .report-mockup-wrapper .mini-bar-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 9px;
          font-size: 11.5px;
        }
        .report-mockup-wrapper .mini-bar-row .mini-label {
          color: var(--ink-muted, #5B6472);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
        }
        .report-mockup-wrapper.theme-dark .mini-bar-row .mini-label {
          color: var(--text-muted);
        }
        .report-mockup-wrapper .mini-bar-row .mini-track {
          flex: 1;
          height: 5px;
          background: var(--border, #E4E7ED);
          border-radius: 3px;
          overflow: hidden;
        }
        .report-mockup-wrapper.theme-dark .mini-bar-row .mini-track {
          background: var(--line);
        }
        .report-mockup-wrapper .mini-bar-row .mini-fill {
          display: block;
          height: 100%;
          background: var(--purple);
          border-radius: 3px;
        }
        .report-mockup-wrapper.theme-light .mini-bar-row .mini-val {
          font-weight: 600;
          color: var(--navy);
          font-size: 11.5px;
        }
        .report-mockup-wrapper.theme-dark .mini-bar-row .mini-val {
          font-weight: 700;
          color: #ffffff;
          font-size: 11.5px;
        }
        .report-mockup-wrapper .ai-stat-line {
          display: flex;
          justify-content: space-between;
          font-size: 12.5px;
          padding: 6px 0;
          border-top: 1px solid var(--border, #E4E7ED);
        }
        .report-mockup-wrapper.theme-dark .ai-stat-line {
          border-top: 1px solid var(--line);
        }
        .report-mockup-wrapper .ai-stat-line:first-child { border-top: none; }
        .report-mockup-wrapper .ai-stat-line span { color: var(--ink-muted, #5B6472); }
        .report-mockup-wrapper.theme-dark .ai-stat-line span { color: var(--text-muted); }
        .report-mockup-wrapper.theme-light .ai-stat-line strong { color: var(--navy); }
        .report-mockup-wrapper.theme-dark .ai-stat-line strong { color: #ffffff; }

        /* Work Groups */
        .report-mockup-wrapper .report-work-groups {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 24px;
        }
        .report-mockup-wrapper.theme-dark .report-work-groups {
          gap: 16px;
        }
        .report-mockup-wrapper.theme-dark .report-work-group {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 16px 18px;
        }
        .report-mockup-wrapper .report-work-group h4 {
          font-size: 12.5px;
          color: var(--ink-muted, #5B6472);
          font-weight: 600;
          margin: 0 0 12px;
        }
        .report-mockup-wrapper.theme-dark .report-work-group h4 {
          font-size: 11px;
          color: var(--text-faint);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 700;
        }
        .report-mockup-wrapper .report-work-group ul { list-style: none; margin: 0; padding: 0; }
        .report-mockup-wrapper .report-work-group li {
          font-size: 13px;
          color: var(--ink, #1B2230);
          padding: 8px 0;
          border-top: 1px solid var(--border, #E4E7ED);
          display: flex;
          gap: 9px;
        }
        .report-mockup-wrapper.theme-dark .report-work-group li {
          color: var(--text);
          border-top: 1px solid var(--line);
        }
        .report-mockup-wrapper .report-work-group li:first-child { border-top: none; }
        .report-mockup-wrapper .report-work-group li svg { flex-shrink: 0; margin-top: 2px; }

        /* Priority List */
        .report-mockup-wrapper .report-priority-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .report-mockup-wrapper .report-priority-list.two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 32px;
        }
        .report-mockup-wrapper .report-priority-list.two-col .report-priority {
          padding: 14px 0;
        }
        .report-mockup-wrapper .report-priority {
          display: flex;
          gap: 16px;
          padding: 16px 0;
          border-top: 1px solid var(--border, #E4E7ED);
        }
        .report-mockup-wrapper.theme-dark .report-priority {
          border-top: 1px solid var(--line);
        }
        .report-mockup-wrapper .report-priority:first-child { border-top: none; padding-top: 0; }
        .report-mockup-wrapper .report-priority .sev {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          margin-top: 7px;
          flex-shrink: 0;
        }
        .report-mockup-wrapper .report-priority .sev.high { background: var(--red); }
        .report-mockup-wrapper .report-priority .sev.med { background: var(--amber); }
        .report-mockup-wrapper .report-priority .sev.low { background: var(--green); }
        .report-mockup-wrapper.theme-light .report-priority .p-title {
          font-size: 14.5px;
          font-weight: 600;
          color: var(--navy);
          margin: 0 0 4px;
        }
        .report-mockup-wrapper.theme-dark .report-priority .p-title {
          font-size: 14px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 4px;
        }
        .report-mockup-wrapper .report-priority .p-detail {
          font-size: 13px;
          color: var(--ink-muted, #5B6472);
          line-height: 1.6;
          margin: 0;
        }
        .report-mockup-wrapper.theme-dark .report-priority .p-detail {
          font-size: 12.5px;
          color: var(--text-muted);
        }

        /* Growth Plan */
        .report-mockup-wrapper.theme-light .report-growth-plan {
          background: var(--navy);
          background-image: linear-gradient(135deg, var(--navy) 0%, #23205C 100%);
          border-radius: 16px;
          padding: 38px 38px 32px;
          color: #fff;
          margin-top: 18px;
        }
        .report-mockup-wrapper.theme-dark .report-growth-plan {
          background: linear-gradient(135deg, #141C36 0%, #241C4A 100%);
          border: 1px solid var(--line);
          border-radius: 12px;
          padding: 38px 38px 32px;
          color: #fff;
          margin-top: 18px;
        }
        .report-mockup-wrapper.theme-light .report-growth-plan h2 {
          font-family: 'Fraunces', serif;
          font-size: 25px;
          font-weight: 600;
          margin: 0 0 8px;
          color: #ffffff;
        }
        .report-mockup-wrapper.theme-dark .report-growth-plan h2 {
          font-size: 24px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 8px;
        }
        .report-mockup-wrapper .report-growth-plan .gp-sub {
          font-size: 14px;
          color: #AEB9CC;
          margin: 0 0 28px;
        }
        .report-mockup-wrapper.theme-dark .report-growth-plan .gp-sub {
          font-size: 13.5px;
          color: var(--text-faint);
        }
        .report-mockup-wrapper .report-gp-groups {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 26px 34px;
        }
        .report-mockup-wrapper .report-gp-group .gp-label {
          font-size: 11.5px;
          color: var(--cyan);
          font-weight: 600;
          margin-bottom: 9px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .report-mockup-wrapper.theme-dark .report-gp-group .gp-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
        }
        .report-mockup-wrapper .report-gp-group ul { margin: 0; padding: 0; list-style: none; }
        .report-mockup-wrapper.theme-light .report-gp-group li {
          font-size: 13.5px;
          color: #E7EAF2;
          padding: 7px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
          line-height: 1.5;
        }
        .report-mockup-wrapper.theme-dark .report-gp-group li {
          font-size: 13.5px;
          color: var(--text-muted);
          padding: 8px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          line-height: 1.55;
        }
        .report-mockup-wrapper .report-gp-group li:first-child { border-top: none; padding-top: 0; }

        /* Footer */
        .report-mockup-wrapper footer.report-footer {
          padding: 38px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: var(--ink-faint, #8A93A3);
          flex-wrap: wrap;
          gap: 8px;
        }
        .report-mockup-wrapper.theme-dark footer.report-footer {
          border-top: 1px solid var(--line);
          margin-top: 20px;
          padding-top: 24px;
          color: var(--text-faint);
          font-size: 11.5px;
        }
        .report-mockup-wrapper .note-inline {
          font-size: 12px;
          color: var(--ink-faint, #8A93A3);
          margin: -10px 0 20px;
        }
        .report-mockup-wrapper.theme-dark .note-inline {
          font-size: 11.5px;
          color: var(--text-faint);
          font-style: italic;
        }

        @media (max-width: 640px) {
          .report-mockup-wrapper .report-hero h1 { font-size: 30px; }
          .report-mockup-wrapper .report-stat { flex: 1 1 45%; border-right: none; border-bottom: 1px solid var(--border, #E4E7ED); padding: 14px 0; }
          .report-mockup-wrapper .report-work-groups { grid-template-columns: 1fr; }
          .report-mockup-wrapper .report-gp-groups { grid-template-columns: 1fr; }
          .report-mockup-wrapper .report-growth-plan { padding: 26px 22px; }
          .report-mockup-wrapper .report-check-grid { grid-template-columns: 1fr; }
          .report-mockup-wrapper .report-priority-list.two-col { grid-template-columns: 1fr; }
          .report-mockup-wrapper .ai-widget-grid { grid-template-columns: 1fr; }
          .report-mockup-wrapper table.report-table { font-size: 12px; }
        }
      `}} />

      {/* Top Band */}
      <div className="brand-band">
        <div className="brand-band-inner">
          <div className="brand-mark">
            <img
              src="/images/seenbyai.png"
              alt="SeenByAI"
              style={{
                height: "32px",
                width: "auto",
                objectFit: "contain"
              }}
            />
          </div>
          <div className="prepared-for">
            Report prepared for
            <strong>{companyName}</strong>
          </div>
        </div>
      </div>
      <div className="accent-rule"></div>

      {/* Inner Report Page */}
      <div className="report-page">

        {/* Topbar */}
        <div className="report-topbar">
          <div className="report-agency-mark">
            {agencyLogoUrl ? (
              <SafeLogo
                src={agencyLogoUrl}
                alt={agencyName || "Agency Logo"}
                style={{ height: isDark ? "30px" : "30px", maxHeight: "30px", maxWidth: "140px", objectFit: "contain" }}
                fallback={
                  agencyName ? (
                    <div
                      className="report-logo-swatch"
                      style={{
                        background: isDark
                          ? "linear-gradient(135deg, var(--cyan), var(--purple))"
                          : `linear-gradient(135deg, var(--navy), ${accentColor})`
                      }}
                    ></div>
                  ) : null
                }
              />
            ) : agencyName ? (
              <div
                className="report-logo-swatch"
                style={{
                  background: isDark
                    ? "linear-gradient(135deg, var(--cyan), var(--purple))"
                    : `linear-gradient(135deg, var(--navy), ${accentColor})`
                }}
              ></div>
            ) : null}
            {agencyName ? <span>{agencyName}</span> : null}
          </div>
          <div className="report-powered-by">{companyName} &middot; {reportMonth}</div>
        </div>

        {/* Data Key */}
        <div className="report-data-key">
          <span><i className="sw" style={{ background: "var(--cyan)" }}></i>Google data (GA4 / GSC)</span>
          <span><i className="sw" style={{ background: "var(--purple)" }}></i>SeenByAI intelligence</span>
          <span><i className="sw" style={{ background: isDark ? "var(--text-faint)" : "var(--ink-faint)" }}></i>Third-party data (backlink provider)</span>
        </div>

        {/* Hero */}
        <div className="report-hero">
          <div className="report-hero-header">
            <div className="report-hero-text">
              <p className="report-eyebrow">Monthly Search &amp; AI Visibility Report</p>
              <h1>{companyName}</h1>
              <p className="report-client-line">{periodLabel}</p>
              {isDark ? (
                <div className="report-status-pill dark-pill">
                  <span className="pill-label">Status</span>
                  <span className="pill-divider"></span>
                  <span className="pill-val">{statusPill}</span>
                </div>
              ) : (
                <div className="report-status-pill">
                  <div className="dot"></div>
                  <span>{statusPill}</span>
                </div>
              )}
            </div>
            {companyLogoUrl && (
              <div className="report-hero-logo">
                <SafeLogo
                  src={companyLogoUrl}
                  alt={companyName}
                  style={{ maxHeight: "60px", maxWidth: "220px", objectFit: "contain", borderRadius: "8px", display: "block" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Executive Summary */}
        <div className="report-exec">
          <p>{execSummary}</p>
          <div className="report-chip-row">
            {chips.map((chip, idx) => (
              <div key={idx} className={`report-chip ${chip.type === "watch" ? "watch" : "win"}`}>
                <div className="chip-dot"></div>
                {chip.text}
              </div>
            ))}
          </div>
        </div>

        {/* 01 Website & Traffic Performance */}
        <section className="report-section sec-website">
          <div className="report-section-head src-google">
            <div className="report-section-head-text">
              <span className="report-section-num">01</span>
              <h2>Website &amp; Traffic Performance</h2>
            </div>
            <span className="report-section-source">Google Analytics</span>
          </div>

          <div className="report-stat-strip">
            {sec01Stats.map((st, idx) => (
              <div key={idx} className="report-stat">
                <div className="stat-label">{st.label}</div>
                <div className="stat-value">{st.value}</div>
                {st.delta && (
                  <div className={`stat-delta ${st.direction || "flat"}`}>{st.delta}</div>
                )}
              </div>
            ))}
          </div>

          {trafficNarrative && (
            <p className="report-narrative">{trafficNarrative}</p>
          )}

          <div className="subsection-block">
            <div className="subsection-title">Traffic by channel</div>
            {channels.length > 0 ? (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Period</th>
                      <th>Total users</th>
                      <th>New users</th>
                      <th>Returning users</th>
                      <th>Sessions</th>
                      <th>Engaged sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((ch, idx) => (
                      <tr key={idx}>
                        <td>{ch.channel}</td>
                        <td className="num">
                          <span className="table-period-badge">{ch.period}</span>
                        </td>
                        <td className="num">{Number(ch.total_users || 0).toLocaleString()}</td>
                        <td className="num">{Number(ch.new_users || 0).toLocaleString()}</td>
                        <td className="num">{Number(ch.returning_users || 0).toLocaleString()}</td>
                        <td className="num">{Number(ch.sessions || 0).toLocaleString()}</td>
                        <td className="num">{Number(ch.engaged_sessions || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="note-inline">No channel traffic breakdown recorded in Google Analytics for this property in this period.</p>
            )}
          </div>

          <div className="subsection-block">
            <div className="subsection-title">Users by country</div>
            {countries.length > 0 ? (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Period</th>
                      <th>Active users</th>
                      <th>New users</th>
                      <th>Engaged sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map((ct, idx) => (
                      <tr key={idx}>
                        <td>{ct.country}</td>
                        <td className="num">
                          <span className="table-period-badge">{ct.period}</span>
                        </td>
                        <td className="num">{Number(ct.active_users || 0).toLocaleString()}</td>
                        <td className="num">{Number(ct.new_users || 0).toLocaleString()}</td>
                        <td className="num">{Number(ct.engaged_sessions || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="note-inline">No geographic visitor data recorded in Google Analytics for this property in this period.</p>
            )}
          </div>
        </section>

        {/* 02 Google Search Performance */}
        <section className="report-section sec-search">
          <div className="report-section-head src-google">
            <div className="report-section-head-text">
              <span className="report-section-num">02</span>
              <h2>Google Search Performance</h2>
            </div>
            <span className="report-section-source">Search Console</span>
          </div>

          <div className="report-stat-strip">
            {sec02Stats.map((st, idx) => (
              <div key={idx} className="report-stat">
                <div className="stat-label">{st.label}</div>
                <div className="stat-value">{st.value}</div>
                {st.delta && (
                  <div className={`stat-delta ${st.direction || "flat"}`}>{st.delta}</div>
                )}
              </div>
            ))}
          </div>

          {gscNarrative && (
            <p className="report-narrative">{gscNarrative}</p>
          )}

          <div className="subsection-block">
            <div className="subsection-title">Top queries</div>
            {topQueries.length > 0 ? (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Query</th>
                      <th>Clicks</th>
                      <th>Impressions</th>
                      <th>Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topQueries.map((q, idx) => (
                      <tr key={idx}>
                        <td>{q.query}</td>
                        <td className="num">{Number(q.clicks || 0).toLocaleString()}</td>
                        <td className="num">{Number(q.impressions || 0).toLocaleString()}</td>
                        <td className="num">{q.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="note-inline">{queriesUnavailableReason}</p>
            )}
          </div>

          <div className="subsection-block">
            <div className="subsection-title">Top pages</div>
            {topPages.length > 0 ? (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Clicks</th>
                      <th>Impressions</th>
                      <th>CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPages.map((p, idx) => (
                      <tr key={idx}>
                        <td>{p.page}</td>
                        <td className="num">{Number(p.clicks || 0).toLocaleString()}</td>
                        <td className="num">{Number(p.impressions || 0).toLocaleString()}</td>
                        <td className="num">{p.ctr}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="note-inline">{pagesUnavailableReason}</p>
            )}
          </div>
        </section>

        {/* 03 AI Readiness & Knowledge Engine */}
        <section className="report-section">
          <div className="report-section-head src-ai">
            <div className="report-section-head-text">
              <span className="report-section-num">03</span>
              <h2>AI Readiness &amp; Knowledge Engine</h2>
            </div>
            <span className="report-section-source">SeenByAI intelligence</span>
          </div>

          {aiNarrative && (
            <p className="report-narrative">{aiNarrative}</p>
          )}

          <div className="report-score-block">
            <div className="report-score-ring">
              <svg viewBox="0 0 110 110">
                <circle cx="55" cy="55" r="46" fill="none" stroke={isDark ? "var(--line)" : "#E4E7ED"} strokeWidth="10" />
                <circle
                  cx="55"
                  cy="55"
                  r="46"
                  fill="none"
                  stroke={isDark ? "var(--cyan)" : "#1FB6C4"}
                  strokeWidth="10"
                  strokeDasharray="289"
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 55 55)"
                />
              </svg>
              <div className="report-score-num">
                <div className="n">{score}</div>
                <div className="l">/100 &middot; {tier}</div>
              </div>
            </div>

            <div className="report-score-bars">
              {categories.map((cat, idx) => (
                <div key={idx} className="report-score-bar-row">
                  <span className="b-label">{cat.label}</span>
                  <span className="b-track">
                    <span className="b-fill" style={{ width: `${cat.score}%`, background: cat.color }}></span>
                  </span>
                  <span className="b-val">{cat.score}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="subsection-title">Scanner findings</div>
          <div className="report-check-grid">
            {checklist.map((item, idx) => (
              <div key={idx} className="report-check-item">
                <div className="ci-label">
                  {item.name}
                  <span className="ci-sub">{item.sub}</span>
                </div>
                <span className={`status-tag ${item.status}`}>
                  {item.status === "found" ? "Found" : "Missing"}
                </span>
              </div>
            ))}
          </div>

          <div className="subsection-title">Knowledge Engine progress</div>
          <div className="report-stat-strip">
            {keStats.map((st, idx) => (
              <div key={idx} className="report-stat">
                <div className="stat-label">{st.label}</div>
                <div className="stat-value" style={st.status_color ? { color: st.status_color, fontSize: "18px" } : {}}>
                  {st.value}
                </div>
                {st.delta && (
                  <div className={`stat-delta ${st.direction || "flat"}`}>{st.delta}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 04 AI Referral Traffic & Potential Leads */}
        <section className="report-section">
          <div className="report-section-head src-ai">
            <div className="report-section-head-text">
              <span className="report-section-num">04</span>
              <h2>AI Referral Traffic &amp; Potential Leads</h2>
            </div>
            <span className="report-section-source">Google Analytics, AI-classified</span>
          </div>

          <div className="report-stat-strip">
            {sec05Stats.map((st, idx) => (
              <div key={idx} className="report-stat">
                <div className="stat-label">{st.label}</div>
                <div className="stat-value">{st.value}</div>
                {st.delta && (
                  <div className={`stat-delta ${st.direction || "flat"}`}>{st.delta}</div>
                )}
              </div>
            ))}
          </div>

          <div className="subsection-title">AI-driven potential leads</div>
          {aiLeads.length > 0 ? (
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>AI platform</th>
                    <th>Recommended page</th>
                    <th>Potential lead</th>
                    <th>Date</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {aiLeads.map((lead, idx) => (
                    <tr key={idx}>
                      <td>{lead.platform}</td>
                      <td>{lead.recommended_page}</td>
                      <td>{lead.potential_lead}</td>
                      <td className="num">{lead.date}</td>
                      <td>{lead.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="note-inline">No AI-driven potential leads or conversions recorded in Google Analytics for this period.</p>
          )}

          {aiSessionsCount > 0 && aiLandingPages.length > 0 ? (
            <div className="ai-widget-grid">
              <div className="ai-widget">
                <div className="ai-widget-title">AI landing pages</div>
                {aiLandingPages.slice(0, 3).map((lp, idx) => (
                  <div key={idx} className="mini-bar-row">
                    <span className="mini-label">{lp.page}</span>
                    <span className="mini-track">
                      <span className="mini-fill" style={{ width: `${Math.min(100, Math.round((lp.sessions / maxLpSessions) * 100))}%` }}></span>
                    </span>
                    <span className="mini-val">{lp.sessions}</span>
                  </div>
                ))}
              </div>
              <div className="ai-widget">
                <div className="ai-widget-title">AI &rarr; key events</div>
                <div className="ai-stat-line"><span>AI sessions</span><strong>{aiSessionsCount}</strong></div>
                <div className="ai-stat-line"><span>Engaged sessions</span><strong>{engagedSessionsCount}</strong></div>
                <div className="ai-stat-line"><span>Key events</span><strong>{eventsCount}</strong></div>
              </div>
              <div className="ai-widget">
                <div className="ai-widget-title">Top pages driving AI traffic</div>
                {aiLandingPages.slice(0, 3).map((lp, idx) => (
                  <div key={idx} className="mini-bar-row">
                    <span className="mini-label">{idx + 1}. {lp.page}</span>
                    <span className="mini-val" style={{ marginLeft: "auto" }}>{lp.sessions}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="note-inline">No AI referral landing page data available because 0 AI sessions were recorded in this period.</p>
          )}
        </section>

        {/* 05 Work Completed This Month */}
        <section className="report-section">
          <div className="report-section-head">
            <div className="report-section-head-text">
              <span className="report-section-num">05</span>
              <h2>Work Completed This Month</h2>
            </div>
          </div>
          <div className="report-work-groups">
            <div className="report-work-group">
              <h4>On-page &amp; content</h4>
              <ul>
                {onPage.length > 0 ? (
                  onPage.map((item, idx) => (
                    <li key={idx}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M4 12l5 5L20 6" stroke={isDark ? "var(--green)" : "#1E9E6B"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {item}
                    </li>
                  ))
                ) : (
                  <li><span style={{ color: isDark ? "var(--text-faint)" : "var(--ink-faint)" }}>No on-page actions logged this period.</span></li>
                )}
              </ul>
            </div>

            <div className="report-work-group">
              <h4>Off-page SEO</h4>
              <ul>
                {offPage.length > 0 ? (
                  offPage.map((item, idx) => (
                    <li key={idx}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M4 12l5 5L20 6" stroke={isDark ? "var(--green)" : "#1E9E6B"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {item}
                    </li>
                  ))
                ) : (
                  <li><span style={{ color: isDark ? "var(--text-faint)" : "var(--ink-faint)" }}>No off-page campaigns active this period.</span></li>
                )}
              </ul>
            </div>

            <div className="report-work-group">
              <h4>Technical &amp; AI readiness</h4>
              <ul>
                {technical.length > 0 ? (
                  technical.map((item, idx) => (
                    <li key={idx}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M4 12l5 5L20 6" stroke={isDark ? "var(--green)" : "#1E9E6B"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {item}
                    </li>
                  ))
                ) : (
                  <li><span style={{ color: isDark ? "var(--text-faint)" : "var(--ink-faint)" }}>No technical actions logged this period.</span></li>
                )}
              </ul>
            </div>
          </div>
        </section>

        {/* 06 Next Month's Priorities */}
        <section className="report-section">
          <div className="report-section-head">
            <div className="report-section-head-text">
              <span className="report-section-num">06</span>
              <h2>Next Month's Priorities</h2>
            </div>
          </div>
          <div className="report-priority-list two-col">
            {priorities.length > 0 ? (
              priorities.map((pr, idx) => (
                <div key={idx} className="report-priority">
                  <div className={`sev ${pr.severity || "med"}`}></div>
                  <div>
                    <p className="p-title">{pr.title}</p>
                    <p className="p-detail">{pr.detail}</p>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: isDark ? "var(--text-muted)" : "var(--ink-muted)", fontSize: "13px", padding: "12px 0" }}>No active priority items defined.</div>
            )}
          </div>
        </section>

        {/* Growth Plan */}
        {!isGpHidden && gpSections.length > 0 && (
          <div className="report-growth-plan">
            <h2>{gpTitle}</h2>
            {gpSubtitle && <p className="gp-sub">{gpSubtitle}</p>}
            <div className="report-gp-groups">
              {gpSections.map((sec, sIdx) => (
                <div key={sec.id || sIdx} className="report-gp-group">
                  <div className="gp-label">{sec.title}</div>
                  <ul>
                    {sec.items && sec.items.length > 0 ? (
                      sec.items.map((item, idx) => <li key={idx}>{item}</li>)
                    ) : (
                      <li>No items listed.</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="report-footer">
          <span>Prepared {agencyName ? `by ${agencyName} ` : ""}for {companyName}</span>
          <span>{agencyName && agencyName !== "SeenByAI" ? `Report produced by ${agencyName}` : (agencyLogoUrl || !agencyName ? `Report prepared for ${companyName}` : "Report generated with SeenByAI")}</span>
        </footer>

      </div>
    </div>
  );
}
